import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Avatar,
  Chip,
  Button,
  Switch,
  Divider,
  Box,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  AdminPanelSettings,
  Email,
  Badge,
  Security,
  VerifiedUser,
  CheckCircle,
  AccountBalanceWallet,
  HourglassEmpty,
  ExitToApp,
  NotificationsActive,
  VpnKey,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "lib/store/slice/authSlice";
import { LOCAL_STORAGE } from "utils/constant";
import toast from "react-hot-toast";

function Overview() {
  const dispatch = useDispatch();
  const authStateUser = useSelector((state) => state.auth?.user);

  const [adminUser, setAdminUser] = useState({
    name: "System Super Admin",
    email: "admin@circuitscramble.com",
    role: "System Administrator",
    picture: "",
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE.USER_INFO);
      if (stored) {
        const parsed = typeof stored === "string" ? JSON.parse(stored) : stored;
        setAdminUser({
          name: parsed.name || "System Super Admin",
          email: parsed.email || "admin@circuitscramble.com",
          role: parsed.role || "System Administrator",
          picture: parsed.picture || "",
        });
      } else if (authStateUser) {
        const parsed = typeof authStateUser === "string" ? JSON.parse(authStateUser) : authStateUser;
        setAdminUser({
          name: parsed.name || "System Super Admin",
          email: parsed.email || "admin@circuitscramble.com",
          role: parsed.role || "System Administrator",
          picture: parsed.picture || "",
        });
      }
    } catch (err) {
      console.error("Error reading admin user profile:", err);
    }
  }, [authStateUser]);

  // System Notification Toggles
  const [notifyPending, setNotifyPending] = useState(true);
  const [notifyTopup, setNotifyTopup] = useState(true);
  const [notifySecurity, setNotifySecurity] = useState(true);

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <DashboardLayout>
      <DashboardNavbar light />
      
      {/* Top Cyber Profile Header Banner */}
      <VuiBox mt={3} mb={3}>
        <Card sx={{ p: 3, backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "20px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
            <VuiBox display="flex" alignItems="center" gap={3}>
              <Avatar
                src={adminUser.picture}
                alt={adminUser.name}
                sx={{
                  width: 80,
                  height: 80,
                  border: "2px solid #06b6d4",
                  boxShadow: "0 0 16px rgba(6, 182, 212, 0.5)",
                  backgroundColor: "#0f172a",
                  fontSize: "28px",
                  fontWeight: "bold",
                  color: "#38bdf8",
                }}
              >
                {adminUser.name ? adminUser.name.charAt(0).toUpperCase() : "A"}
              </Avatar>
              <VuiBox>
                <VuiBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">
                    {adminUser.name}
                  </VuiTypography>
                  <Chip
                    icon={<VerifiedUser sx={{ color: "#ffffff !important", fontSize: "14px !important" }} />}
                    label="VERIFIED ADMIN"
                    size="small"
                    sx={{
                      backgroundColor: "#06b6d4 !important",
                      color: "#ffffff !important",
                      fontWeight: "bold",
                      fontSize: "10px",
                    }}
                  />
                </VuiBox>
                <VuiTypography variant="button" color="text" fontWeight="medium">
                  {adminUser.email}
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            <Button
              variant="contained"
              startIcon={<ExitToApp />}
              onClick={handleLogout}
              sx={{
                backgroundColor: "#f43f5e !important",
                color: "#ffffff !important",
                fontWeight: "bold",
                borderRadius: "10px",
                px: 3,
                py: 1,
                boxShadow: "0 0 12px rgba(244, 63, 94, 0.4)",
                "&:hover": {
                  backgroundColor: "#e11d48 !important",
                },
              }}
            >
              Sign Out
            </Button>
          </VuiBox>
        </Card>
      </VuiBox>

      {/* Main Grid: Details, Privileges & System Settings */}
      <VuiBox mb={3}>
        <Grid container spacing={3}>
          {/* Card 1: Admin Account Information */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ p: 3, backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "20px", height: "100%" }}>
              <VuiBox display="flex" alignItems="center" gap={1.5} mb={2}>
                <AdminPanelSettings sx={{ color: "#38bdf8" }} />
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Administrator Profile
                </VuiTypography>
              </VuiBox>

              <VuiBox display="flex" flexDirection="column" gap={2}>
                <VuiBox p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <VuiTypography variant="caption" color="text" fontWeight="bold">
                    FULL NAME
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white" fontWeight="bold">
                    {adminUser.name}
                  </VuiTypography>
                </VuiBox>

                <VuiBox p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <VuiTypography variant="caption" color="text" fontWeight="bold">
                    EMAIL ADDRESS
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white" fontWeight="bold">
                    {adminUser.email}
                  </VuiTypography>
                </VuiBox>

                <VuiBox p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <VuiTypography variant="caption" color="text" fontWeight="bold">
                    ROLE & ACCESS LEVEL
                  </VuiTypography>
                  <VuiTypography variant="body2" sx={{ color: "#38bdf8 !important" }} fontWeight="bold">
                    Super Administrator (Full System Control)
                  </VuiTypography>
                </VuiBox>

                <VuiBox p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <VuiTypography variant="caption" color="text" fontWeight="bold">
                    AUTH SESSION
                  </VuiTypography>
                  <VuiTypography variant="body2" sx={{ color: "#34d399 !important" }} fontWeight="bold">
                    🟢 Active OAuth Google Session
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>

          {/* Card 2: System Administrative Privileges */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ p: 3, backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "20px", height: "100%" }}>
              <VuiBox display="flex" alignItems="center" gap={1.5} mb={2}>
                <Security sx={{ color: "#10b981" }} />
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  System Privileges
                </VuiTypography>
              </VuiBox>

              <VuiBox display="flex" flexDirection="column" gap={2}>
                <VuiBox display="flex" alignItems="center" gap={1.5} p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <CheckCircle sx={{ color: "#10b981", fontSize: 20 }} />
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      Community Level Approval & Rejection
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block">
                      Full moderation control over arcade level queue.
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>

                <VuiBox display="flex" alignItems="center" gap={1.5} p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <CheckCircle sx={{ color: "#10b981", fontSize: 20 }} />
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      Coin Economy & Top-Up Approvals
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block">
                      Grant Logic Coins to creators & process requests.
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>

                <VuiBox display="flex" alignItems="center" gap={1.5} p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <CheckCircle sx={{ color: "#10b981", fontSize: 20 }} />
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      Classic Campaign Creation
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block">
                      Design & publish official levels with reward coins.
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>

                <VuiBox display="flex" alignItems="center" gap={1.5} p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <CheckCircle sx={{ color: "#10b981", fontSize: 20 }} />
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      User Account Directory Access
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block">
                      Inspect registered player accounts and statistics.
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>

          {/* Card 3: Notifications & Preferences */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ p: 3, backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "20px", height: "100%" }}>
              <VuiBox display="flex" alignItems="center" gap={1.5} mb={2}>
                <NotificationsActive sx={{ color: "#f59e0b" }} />
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  System Preferences
                </VuiTypography>
              </VuiBox>

              <VuiBox display="flex" flexDirection="column" gap={2}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      Pending Submissions Alerts
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block">
                      Notify when creators submit levels for review.
                    </VuiTypography>
                  </VuiBox>
                  <Switch
                    checked={notifyPending}
                    onChange={(e) => setNotifyPending(e.target.checked)}
                    color="info"
                  />
                </VuiBox>

                <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      Coin Top-Up Notifications
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block">
                      Alert when creators request coin top-ups.
                    </VuiTypography>
                  </VuiBox>
                  <Switch
                    checked={notifyTopup}
                    onChange={(e) => setNotifyTopup(e.target.checked)}
                    color="info"
                  />
                </VuiBox>

                <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: "#0f172a", borderRadius: "12px", border: "1px solid #334155" }}>
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      Security & Activity Logs
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block">
                      Track admin session activity & API queries.
                    </VuiTypography>
                  </VuiBox>
                  <Switch
                    checked={notifySecurity}
                    onChange={(e) => setNotifySecurity(e.target.checked)}
                    color="info"
                  />
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>

      <Footer />
    </DashboardLayout>
  );
}

export default Overview;
